import { useEffect, useMemo, useState } from 'react';

import { hydrateBestLoanPlannerSnapshot } from '../core/storage/loanPlannerStorage';
import {
  HOME_LOAN_PLANNER_ID,
  loanPlannerLookupIds,
  resolveLoanPlannerInputs,
} from '../utils/loanPlannerSeed';
import {
  calculateMonthlyPayment,
  estimatePaymentSplit,
} from '../utils/loanAmortization';
import { useLoanSnapshot } from './useLoanSnapshot';

export type LoanPlannerSummary = {
  hydrated: boolean;
  /** Snapshot or household mortgage — safe to show a filled widget. */
  hasData: boolean;
  label: string;
  principal: number;
  interestRatePct: number;
  termYears: number;
  monthlyPayment: number;
  monthlyInterest: number;
  monthlyPrincipal: number;
  interestSharePct: number;
  principalSharePct: number;
  hasExtras: boolean;
  startMonth: string | null;
};

/**
 * Light loan summary for Home/Wealth widgets.
 * Hydrates synced planner data (including a partner's sole household loan).
 */
export function useLoanPlannerSummary(): LoanPlannerSummary {
  const { mortgage } = useLoanSnapshot();

  const [hydrated, setHydrated] = useState(false);
  const [principal, setPrincipal] = useState(0);
  const [interestRatePct, setInterestRatePct] = useState(0);
  const [termYears, setTermYears] = useState(30);
  const [label, setLabel] = useState('Home loan');
  const [hasData, setHasData] = useState(false);
  const [hasExtras, setHasExtras] = useState(false);
  const [startMonth, setStartMonth] = useState<string | null>(null);

  const mortgageId = mortgage?.id ?? null;
  const mortgageBalance = mortgage?.balance ?? null;

  useEffect(() => {
    let cancelled = false;

    hydrateBestLoanPlannerSnapshot(
      loanPlannerLookupIds(mortgageId),
      HOME_LOAN_PLANNER_ID,
    ).then((snapshot) => {
      if (cancelled) return;
      const next = resolveLoanPlannerInputs(HOME_LOAN_PLANNER_ID, mortgage, snapshot);
      setPrincipal(next.principal);
      setInterestRatePct(next.interestRatePct);
      setTermYears(next.termYears);
      setLabel(next.label);
      setHasData(next.hasSeed);
      setHasExtras(next.recurringExtra > 0 || next.extras.length > 0);
      setStartMonth(next.startMonth);
      setHydrated(true);
    });

    return () => {
      cancelled = true;
    };
    // mortgage object identity changes after setCore — gate on id/balance only.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [mortgageId, mortgageBalance]);

  const termMonths = Math.max(1, Math.round(termYears * 12));
  const monthlyPayment = useMemo(
    () => calculateMonthlyPayment(principal, interestRatePct, termMonths),
    [principal, interestRatePct, termMonths],
  );

  const split = useMemo(
    () => estimatePaymentSplit(principal, interestRatePct, monthlyPayment),
    [principal, interestRatePct, monthlyPayment],
  );

  const interestSharePct =
    monthlyPayment > 0
      ? Math.round((split.monthlyInterest / monthlyPayment) * 100)
      : 0;
  const principalSharePct = Math.max(0, 100 - interestSharePct);

  return {
    hydrated,
    hasData,
    label,
    principal,
    interestRatePct,
    termYears,
    monthlyPayment,
    monthlyInterest: split.monthlyInterest,
    monthlyPrincipal: split.monthlyPrincipal,
    interestSharePct,
    principalSharePct,
    hasExtras,
    startMonth,
  };
}
